<template>
  <div class="configuration-properties">
    <!-- <div class="properties-header">
      <h3>Configuration Properties</h3>
      <div class="properties-actions">
        <Badge :variant="hasChanges ? 'warning' : 'info'">
          {{ hasChanges ? 'Modified' : 'Synchronized' }}
        </Badge>
        <Button
          v-if="hasChanges && !isReadonly"
          variant="primary"
          size="small"
          @click="saveChanges"
          :loading="saving"
        >
          Save Changes
        </Button>
      </div>
    </div> -->
    
    <div v-if="loading" class="properties-loading">
      <ProgressBar :value="100" :indeterminate="true" />
      <span>Loading configuration...</span>
    </div>
    
    <div v-else-if="error" class="properties-error">
      <Icon name="warning-circle" />
      <span>{{ error }}</span>
      <Button variant="secondary" size="small" @click="loadConfiguration">
        Retry
      </Button>
    </div>
    
    <div v-else-if="propertySchemas.length > 0" class="properties-list">
      <div v-if="!hasVaultRegistry && !isReadonly" class="properties-banner">
        <div>
          <strong>Vault registry not configured.</strong>
          <p>Create `_vaults` to manage named vault endpoints for secret references.</p>
        </div>
        <Button variant="secondary" size="small" @click="createVaultRegistry">
          Create Vault Registry
        </Button>
      </div>

      <template v-for="property in propertySchemas" :key="property.name">
        <VaultRegistryEditor
          v-if="property.name === '_vaults' && property.type === 'object'"
          :model-value="getPropertyValue(property.name) || {}"
          :readonly="isReadonly"
          @update:model-value="updateProperty(property.name, $event)"
        />
        <PropertyEditor
          v-else
          :property="property"
          :model-value="getPropertyValue(property.name)"
          :readonly="isReadonly"
          :vault-aliases="vaultAliases"
          @update:model-value="updateProperty(property.name, $event)"
        />
      </template>
    </div>
    
    <div v-else class="properties-empty">
      <Icon name="pencil-simple" />
      <!-- <span>No configuration properties available</span> -->
      <p>Select a tenant to load configuration properties</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import PropertyEditor from '@/components/molecules/PropertyEditor.vue'
import VaultRegistryEditor from '@/components/molecules/VaultRegistryEditor.vue'
// @ts-expect-error - Badge is used in template
import Badge from '@/components/atoms/Badge.vue'
import Button from '@/components/atoms/Button.vue'
import Icon from '@/components/atoms/Icon.vue'
import ProgressBar from '@/components/atoms/ProgressBar.vue'
import { useAuth } from '@/composables/useAuth'
import { useConfigurationStore } from '@/stores/configuration'
import { configurationService } from '@/services/api'
import { withApiNotifications, API_MESSAGES } from '@/utils/api-notifications'

interface PropertySchema {
  name: string
  type: 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'secret'
  description?: string
  default?: any
  enum?: string[]
  minimum?: number
  maximum?: number
  required?: boolean
}

interface Props {
  tenantId?: string
}

const props = defineProps<Props>()

const { canEdit } = useAuth()
const configStore = useConfigurationStore()

// Computed
const isReadonly = computed(() => !canEdit.value || !configStore.isEditMode)
const loading = ref(false)
const saving = ref(false)
const error = ref<string | null>(null)
const originalConfiguration = ref<Record<string, any>>({})
const currentConfiguration = ref<Record<string, any>>({})
const propertySchemas = ref<PropertySchema[]>([])

// Computed
const hasChanges = computed(() => {
  return JSON.stringify(originalConfiguration.value) !== JSON.stringify(currentConfiguration.value)
})

const hasVaultRegistry = computed(() => {
  return Boolean(currentConfiguration.value._vaults && typeof currentConfiguration.value._vaults === 'object' && !Array.isArray(currentConfiguration.value._vaults))
})

const vaultAliases = computed(() => {
  const registry = currentConfiguration.value._vaults
  if (!registry || typeof registry !== 'object' || Array.isArray(registry)) {
    return []
  }

  return Object.keys(registry).sort((left, right) => left.localeCompare(right))
})

function cloneConfiguration<T>(value: T): T {
  if (value === null || value === undefined) {
    return value
  }

  return JSON.parse(JSON.stringify(value)) as T
}

// Methods
function getPropertyValue(propertyName: string) {
  return currentConfiguration.value[propertyName]
}

function updateProperty(propertyName: string, value: any) {
  currentConfiguration.value[propertyName] = value
  // Regenerate property schemas to include the new property
  propertySchemas.value = generatePropertySchemas(currentConfiguration.value)
}

function createVaultRegistry() {
  if (hasVaultRegistry.value) {
    return
  }

  updateProperty('_vaults', {})
}

async function loadConfiguration() {
  if (!props.tenantId) {
    propertySchemas.value = []
    originalConfiguration.value = {}
    currentConfiguration.value = {}
    return
  }
  
  // Use data from the store instead of making duplicate API calls
  const storeConfiguration = configStore.currentConfiguration
  
  if (storeConfiguration && 'tenantId' in storeConfiguration && storeConfiguration.tenantId === props.tenantId) {
    // Use the configuration from the store (ResolvedConfiguration)
    originalConfiguration.value = cloneConfiguration(storeConfiguration.configuration || {})
    currentConfiguration.value = cloneConfiguration(originalConfiguration.value)
    
    // Generate property schemas from the configuration data
    propertySchemas.value = generatePropertySchemas(originalConfiguration.value)
  } else {
    // Only make API call if store doesn't have the right data
    loading.value = true
    error.value = null
    
    try {
      await withApiNotifications(
        async () => {
          const config = await configurationService.getResolvedConfiguration(props.tenantId!)
          
          // Extract configuration data
          originalConfiguration.value = cloneConfiguration(config.configuration || {})
          currentConfiguration.value = cloneConfiguration(originalConfiguration.value)
          
          // Generate property schemas from the configuration data
          propertySchemas.value = generatePropertySchemas(originalConfiguration.value)
          
          return config
        },
        {
          loadingMessage: API_MESSAGES.LOADING.GET_CONFIGURATION,
          successMessage: API_MESSAGES.SUCCESS.GET_CONFIGURATION,
          errorMessage: API_MESSAGES.ERROR.GET_CONFIGURATION,
          showSuccess: false // Don't show success for routine loading
        }
      )
    } catch (err: any) {
      error.value = err.message || 'Failed to load configuration'
      console.error('Configuration loading error:', err)
    } finally {
      loading.value = false
    }
  }
}

// @ts-ignore - Used in template @click handler
async function saveChanges() {
  if (!props.tenantId || !hasChanges.value) return
  
  saving.value = true;
  
  try {
    await withApiNotifications(
      async () => {
        // Create JSON patch operations for the changes
        const operations = createPatchOperations(originalConfiguration.value, currentConfiguration.value);
        
        const result = await configurationService.applyPatch(props.tenantId!, operations);
        
        if (result.success) {
          // Update the original configuration with the new values
          originalConfiguration.value = cloneConfiguration(currentConfiguration.value)
          return result
        } else {
          throw new Error(result.message || 'Failed to save changes')
        }
      },
      {
        loadingMessage: API_MESSAGES.LOADING.APPLY_PATCH,
        successMessage: API_MESSAGES.SUCCESS.APPLY_PATCH,
        errorMessage: API_MESSAGES.ERROR.APPLY_PATCH,
        notificationOptions: {
          source: { id: 'configuration-properties', label: 'Configuration Properties' }
        }
      }
    )
  } catch (err: any) {
    error.value = err.message || 'Failed to save changes'
    console.error('Configuration save error:', err)
  } finally {
    saving.value = false
  }
}

function generatePropertySchemas(config: Record<string, any>): PropertySchema[] {
  return Object.entries(config)
    .map(([key, value]) => {
      const schema: PropertySchema = {
        name: key,
        type: getPropertyType(value),
        description: `Configuration property: ${key}`,
        default: value
      }

      if (typeof value === 'string' && ['true', 'false', 'enabled', 'disabled'].includes(value.toLowerCase())) {
        schema.enum = ['true', 'false']
      }

      return schema
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

function getPropertyType(value: any): 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'secret' {
  if (value && typeof value === 'object' && '$secret' in value) return 'secret'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number'
  if (Array.isArray(value)) return 'array'
  if (value && typeof value === 'object') return 'object'
  return 'string'
}

function createPatchOperations(original: Record<string, any>, current: Record<string, any>) {
  const operations: any[] = []
  
  function compareObjects(orig: any, curr: any, path = '') {
    // Handle added/changed properties
    for (const [key, value] of Object.entries(curr)) {
      const propPath = path ? `${path}/${key}` : `/${key}`
      
      if (!(key in orig)) {
        // Added property
        operations.push({ op: 'add', path: propPath, value })
      } else if (JSON.stringify(orig[key]) !== JSON.stringify(value)) {
        // Changed property
        if (typeof value === 'object' && typeof orig[key] === 'object' && !Array.isArray(value)) {
          compareObjects(orig[key], value, propPath)
        } else {
          operations.push({ op: 'replace', path: propPath, value })
        }
      }
    }
    
    // Handle removed properties
    for (const key of Object.keys(orig)) {
      if (!(key in curr)) {
        const propPath = path ? `${path}/${key}` : `/${key}`
        operations.push({ op: 'remove', path: propPath })
      }
    }
  }
  
  compareObjects(original, current)
  return operations
}

// Watch for tenant changes
watch(() => props.tenantId, loadConfiguration, { immediate: true })
</script>

<style scoped lang="scss">
.configuration-properties {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--vscode-sideBar-background);
}

.properties-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--vscode-widget-border);
  background: var(--vscode-sideBarSectionHeader-background);
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-sideBarSectionHeader-foreground);
  }
}

.properties-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.properties-loading,
.properties-error,
.properties-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: var(--vscode-descriptionForeground);
  height: 100%;
  
  p {
    margin: 0;
    font-size: 12px;
    opacity: 0.7;
  }
}

.properties-list {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.properties-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid var(--vscode-widget-border);
  border-radius: 8px;
  background: var(--vscode-editorWidget-background, rgba(127, 127, 127, 0.06));

  strong {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
  }

  p {
    margin: 0;
    font-size: 12px;
    color: var(--vscode-descriptionForeground);
  }
}
</style>
